import React, { FC, useMemo } from 'react';
import { AlertCircleIcon } from 'lucide-react';

import dayjs from 'utils/extendedDayJS';
import { Button } from '@epoch-ui/components/Button';
import { RadioGroup, RadioGroupItem } from '@epoch-ui/components/RadioGroup/RadioGroup';
import { SessionCard, SessionCardTitleAndDate } from 'components/cards/SessionCard';
import { ModalTitle, ModalFooter } from '@epoch-ui/components/Modal/modal';
import { EventType } from 'types/event.types';
import { GuestStatus } from 'types/guest.types';
import { PlusOneGuest } from 'types/plus-one.types';
import { StepBar } from '@epoch-components/step-bar/StepBar';
import ForwardArrowIcon from 'components/common/icons/ForwardArrowIcon';

import { getDisplayTextForStatus } from '../../../utils';
import { useRSVPControlModalContext } from '../rsvpControlContext';

import { NotificationSettingsSection } from './NotificationSettingsSection';
import { PlusOneStatusNotice } from './PlusOneStatusNotice';
import { SelectRsvpDropdown } from './SelectRsvpDropdown';

const getStatusOptionsForStatus = (
  status: GuestStatus,
  isEventWaitlisted: boolean
): GuestStatus[] => {
  const isWaitlistedAnOption: boolean =
    ![GuestStatus.CONFIRMED, GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY].includes(status) &&
    isEventWaitlisted;
  switch (status) {
    case GuestStatus.CONFIRMED:
      return [
        GuestStatus.INTERESTED,
        GuestStatus.CANCELLED,
        ...(isWaitlistedAnOption ? [GuestStatus.WAITLISTED] : []),
        GuestStatus.INVITED
      ];
    case GuestStatus.INTERESTED:
      return [
        GuestStatus.CONFIRMED,
        GuestStatus.CANCELLED,
        ...(isWaitlistedAnOption ? [GuestStatus.WAITLISTED] : []),
        GuestStatus.INVITED
      ];
    case GuestStatus.WAITLISTED:
      return [
        GuestStatus.CONFIRMED,
        GuestStatus.INTERESTED,
        GuestStatus.CANCELLED,
        GuestStatus.INVITED
      ];
    case GuestStatus.CANCELLED:
      return [
        GuestStatus.CONFIRMED,
        GuestStatus.INTERESTED,
        ...(isWaitlistedAnOption ? [GuestStatus.WAITLISTED] : []),
        GuestStatus.INVITED
      ];
    case GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY:
      return [
        GuestStatus.CONFIRMED,
        GuestStatus.INTERESTED,
        GuestStatus.CANCELLED,
        ...(isWaitlistedAnOption ? [GuestStatus.WAITLISTED] : []),
        GuestStatus.INVITED
      ];
    case GuestStatus.ACTION_REQUIRED:
      return [
        GuestStatus.CONFIRMED,
        GuestStatus.INTERESTED,
        GuestStatus.CANCELLED,
        ...(isWaitlistedAnOption ? [GuestStatus.WAITLISTED] : []),
        GuestStatus.INVITED
      ];
    case GuestStatus.INVITED:
      return [
        GuestStatus.CONFIRMED,
        GuestStatus.INTERESTED,
        GuestStatus.CANCELLED,
        ...(isWaitlistedAnOption ? [GuestStatus.WAITLISTED] : [])
      ];
    default:
      return [
        GuestStatus.CONFIRMED,
        GuestStatus.INTERESTED,
        GuestStatus.CANCELLED,
        ...(isEventWaitlisted ? [GuestStatus.WAITLISTED] : [])
      ];
  }
};

export const SelectRSVPModalContent: FC = () => {
  const {
    navigation,
    guest,
    event,
    selectedStatus,
    setSelectedStatus,
    requiresQuestionnaireSubmission,
    requiresSessionSwapConfirmation,
    requiresWaitlistAdjustment,
    currentSession,
    shouldUpdateAllPlusOnesRsvp,
    setShouldUpdateAllPlusOnesRsvp
  } = useRSVPControlModalContext();

  const shouldShowUpdateAllPlusOnesRadio: boolean = useMemo(() => {
    const plusOnes = (guest.plusOnes || []) as PlusOneGuest[];
    const isCurrentStatusWaitlisted = guest.status === GuestStatus.WAITLISTED;
    const isTargetStatusGoing =
      selectedStatus === GuestStatus.CONFIRMED ||
      selectedStatus === GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY;
    const hasWaitlistedPlusOnes = plusOnes.some(
      (plusOne) => plusOne.status === GuestStatus.WAITLISTED
    );

    return (
      plusOnes.length > 0 &&
      isCurrentStatusWaitlisted &&
      isTargetStatusGoing &&
      hasWaitlistedPlusOnes
    );
  }, [guest.plusOnes, guest.status, selectedStatus]);

  const showNotificationSettings =
    !!selectedStatus &&
    !requiresSessionSwapConfirmation &&
    !requiresQuestionnaireSubmission &&
    !requiresWaitlistAdjustment;

  const eventTimezone = event.timezoneCode || 'UTC';
  const displayedTimezone = dayjs.tz.guess() || eventTimezone;
  const isEventWaitlisted: boolean =
    event.eventType === EventType.MULTI_SESSIONS && !!currentSession
      ? !!currentSession.isEventWaitlisted
      : event.isEventWaitlisted;

  const canSubmit: boolean = !!selectedStatus;
  const statusOptions: GuestStatus[] = useMemo(
    () => getStatusOptionsForStatus(guest.status, isEventWaitlisted),
    [guest.status, isEventWaitlisted]
  );
  const currentStatusText: string = getDisplayTextForStatus(guest.status).toLowerCase();
  const selectedStatusText: string | undefined = selectedStatus
    ? getDisplayTextForStatus(selectedStatus).toLowerCase()
    : undefined;

  const warningSubtextForSelectedStatus = useMemo((): string | undefined => {
    if (!isEventWaitlisted || !selectedStatus || selectedStatus === GuestStatus.CONFIRMED) {
      return undefined;
    }
    if (
      [GuestStatus.CONFIRMED, GuestStatus.CONFIRMED_WITH_INCOMPLETE_SURVEY].includes(guest.status)
    ) {
      return `This event is full. \
      ${guest.firstName} is marked as ${currentStatusText} for this event. \
      They’ll lose their spot if their RSVP is changed to ${selectedStatusText}.`;
    } else if (guest.status === GuestStatus.WAITLISTED) {
      return `${guest.firstName} is currently waitlisted for this event. \
      They’ll lose their spot in line if their RSVP is changed to ${selectedStatusText}.`;
    }
    return undefined;
  }, [isEventWaitlisted, selectedStatus, guest, currentStatusText, selectedStatusText]);

  const renderFooter = (): JSX.Element => {
    if (navigation.totalStepNumber > 1) {
      return (
        <ModalFooter className="tw-mt-0 tw-flex tw-flex-row tw-justify-end">
          <div className="tw-flex tw-flex-1 tw-items-center">
            <StepBar
              totalSteps={navigation.totalStepNumber}
              currentStep={navigation.currentStepNumber}
            />
          </div>
          <div className="tw-flex tw-flex-row tw-justify-center tw-gap-4">
            <Button
              size="sm"
              onClick={navigation.onCancel}
              className="tw-flex-1 tw-rounded-[8px]"
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!canSubmit}
              className="tw-flex-1 tw-rounded-[8px]"
              variant="primary"
              onClick={navigation.onClickSubmit}
            >
              Next
              <div className="tw-ml-2">
                <ForwardArrowIcon color="white" />
              </div>
            </Button>
          </div>
        </ModalFooter>
      );
    } else {
      return (
        <ModalFooter className="tw-mt-0 tw-flex tw-flex-row tw-justify-center tw-gap-4">
          <Button
            onClick={navigation.onCancel}
            className="tw-flex-1 tw-rounded-[8px]"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            loading={navigation.loading}
            className="tw-flex-1 tw-rounded-[8px]"
            variant="primary"
            onClick={navigation.onClickSubmit}
          >
            Update
          </Button>
        </ModalFooter>
      );
    }
  };
  return (
    <>
      <div className="tw-flex tw-flex-col tw-gap-4">
        <ModalTitle className="tw-mb-0 tw-text-2xl tw-font-medium">Change RSVP status</ModalTitle>
        {currentSession && (
          <div className="tw-flex tw-flex-col tw-gap-3">
            <span className="tw-font-medium">
              You’re updating {guest.firstName}’s RSVP for this session:
            </span>
            <SessionCard
              className="tw-cursor-default tw-rounded-[12px]"
              session={currentSession}
              eventTimezone={eventTimezone}
              displayedTimezone={displayedTimezone}
            >
              <SessionCardTitleAndDate />
            </SessionCard>
          </div>
        )}
        <div className="tw-flex tw-flex-col tw-gap-3">
          <span className="tw-font-medium">
            Update {guest.firstName} {guest.lastName}’s RSVP
            {currentSession ? ' for this session' : ''}:
          </span>
          <div className="tw-flex tw-flex-col tw-gap-2">
            <SelectRsvpDropdown
              placeholder={getDisplayTextForStatus(guest.status)}
              statusOptions={statusOptions}
              selectedStatus={selectedStatus}
              onSelect={setSelectedStatus}
            />
            {warningSubtextForSelectedStatus && (
              <div className="tw-flex tw-flex-row tw-gap-1 tw-font-normal tw-text-negative-foreground">
                <div>
                  <AlertCircleIcon size={16} className="tw-pt-0" />
                </div>
                <span className="tw-text-xs">{warningSubtextForSelectedStatus}</span>
              </div>
            )}
          </div>
        </div>
        {shouldShowUpdateAllPlusOnesRadio && (
          <div className="tw-mt-1 tw-flex tw-flex-col tw-gap-3">
            <span className="tw-text-sm tw-font-medium tw-leading-4 tw-text-black">
              {guest.firstName} has +1 guests that are waitlisted. Do you want to update
              everyone&apos;s RSVP?
            </span>
            <RadioGroup
              value={shouldUpdateAllPlusOnesRsvp.toString()}
              onValueChange={(value) => setShouldUpdateAllPlusOnesRsvp(value === 'true')}
            >
              <RadioGroupItem value="true">Yes</RadioGroupItem>
              <RadioGroupItem value="false">No</RadioGroupItem>
            </RadioGroup>
          </div>
        )}
        <PlusOneStatusNotice />
        {showNotificationSettings && <NotificationSettingsSection />}
      </div>
      {renderFooter()}
    </>
  );
};
